import { useState, useRef } from 'react';
import {
  ArrowLeft,
  Box,
  Cpu,
  Key,
  Terminal,
  Scale,
  AlertTriangle,
  ShieldAlert,
  CheckCircle,
  XCircle,
  ExternalLink,
  Info,
  Printer,
  Globe,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/Collapsible';
import guardrailLogo from '@/assets/black-horizontal.svg';
import { getAiBomData, useReportData } from '@/hooks/useReportData';
import { PageSpinner, PageEmptyState, PageBackground } from '@/components/ui/PageLayout';
import { ReportSubHeader } from '@/components/report/ReportSubHeader';
import { AiBomSettings } from '@/components/report/AiBomSettings';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { AIBOMData, AIBOMComponent } from '@/utils/scanToInsights';

interface LLMComponent {
  name: string;
  version: string;
  provider: string;
  purpose: string;
}

interface APIComponent {
  name: string;
  version: string;
  endpoint: string;
  authentication: string;
}

interface DependencyOrigin {
  country?: string;
  countryCode?: string;
  confidence?: 'high' | 'medium' | 'low';
  signals?: string[];
  maintainer?: string;
  maintainerVerified?: boolean;
}

interface LibraryComponent {
  name: string;
  version: string;
  license: string;
  purpose: string;
  vulnerabilities?: VulnerabilityInfo[];
  licenseDetails?: LicenseDetails;
  origin?: DependencyOrigin;
}

interface VulnerabilityInfo {
  cveId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  fixedVersion?: string;
  publishedDate: string;
}

interface LicenseDetails {
  type: 'permissive' | 'copyleft' | 'proprietary' | 'public-domain';
  compatible: boolean;
  limitations: string[];
  requirements: string[];
}

interface SystemTool {
  name: string;
  accessLevel: 'read' | 'write' | 'execute';
  scope: string;
  justification: string;
}

interface AIBomData {
  llmComponents: LLMComponent[];
  apiComponents: APIComponent[];
  libraryComponents: LibraryComponent[];
  systemTools: SystemTool[];
}

/**
 * Union of shapes that the AI-BOM source data can take — covers both
 * the transformed `AIBOMData` from the report (with `components`,
 * `llmProviders`, `byType`) and pre-resolved forms (with
 * `llmComponents`, `apiComponents`, etc.) from window injection.
 */
interface AiBomSource extends AIBOMData {
  llmComponents?: LLMComponent[];
  apiComponents?: APIComponent[];
  libraryComponents?: LibraryComponent[];
  systemTools?: SystemTool[];
}

interface PrintSettings {
  showHeader: boolean;
  showSummaryCards: boolean;
  showAlertBanner: boolean;
  showLLMComponents: boolean;
  showAPIComponents: boolean;
  showLibraries: boolean;
  showVulnerabilities: boolean;
  showLicenseDetails: boolean;
  showSystemTools: boolean;
}

const accessLevelColors = {
  read: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  write: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  execute: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const severityColors = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-yellow-500 text-black',
};

// =============================================================================
// HELPER FUNCTIONS - Extracted to avoid nested ternaries (code optimization)
// =============================================================================

/**
 * Get CSS classes for confidence badge based on confidence level
 */
function getConfidenceBadgeClasses(confidence: string | undefined): string {
  switch (confidence) {
    case 'high':
      return 'bg-emerald-500/10 text-emerald-600';
    case 'medium':
      return 'bg-amber-500/10 text-amber-600';
    default:
      return 'bg-zinc-500/10 text-zinc-600';
  }
}

/**
 * Get CSS classes for vulnerability card based on whether it has issues
 */
function getVulnerabilityCardClasses(hasIssues: boolean): string {
  if (hasIssues) {
    return 'from-red-500/10 to-red-600/5 border-red-500/20';
  }
  return 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20';
}

/**
 * Get CSS classes for license card based on whether it has issues
 */
function getLicenseCardClasses(hasIssues: boolean): string {
  if (hasIssues) {
    return 'from-amber-500/10 to-amber-600/5 border-amber-500/20';
  }
  return 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20';
}

/**
 * Get icon container classes based on issue state
 */
function getIconContainerClasses(hasIssues: boolean, type: 'vulnerability' | 'license'): string {
  if (type === 'vulnerability') {
    return hasIssues ? 'bg-red-500/20' : 'bg-emerald-500/20';
  }
  return hasIssues ? 'bg-amber-500/20' : 'bg-emerald-500/20';
}

/**
 * Get icon classes based on issue state
 */
function getIconClasses(hasIssues: boolean, type: 'vulnerability' | 'license'): string {
  if (type === 'vulnerability') {
    return hasIssues ? 'text-red-600' : 'text-emerald-600';
  }
  return hasIssues ? 'text-amber-600' : 'text-emerald-600';
}

/**
 * Pluralize a word based on count
 */
function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

// =============================================================================
// COMPONENT
// =============================================================================

function AIBOMPage() {
  useDocumentTitle('AI BOM');
  const [expandedVulns, setExpandedVulns] = useState<string[]>([]);
  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    showHeader: true,
    showSummaryCards: true,
    showAlertBanner: true,
    showLLMComponents: true,
    showAPIComponents: true,
    showLibraries: true,
    showVulnerabilities: true,
    showLicenseDetails: true,
    showSystemTools: true,
  });
  const printRef = useRef<HTMLDivElement>(null);

  // Load aiBom data from multiple sources (priority order)
  // 1. useReportData hook (transformed API data)
  // 2. getAiBomData (window.GUARDRAIL_REPORT_DATA.aiBom)
  const { data: reportData, loading } = useReportData();
  const windowAiBom = getAiBomData();

  // Try hook data first, then window injection.
  // The report's aiBom can be either the transformed AIBOMData shape (from API)
  // or the pre-resolved sample shape — AiBomSource covers both.
  const dynamicAiBom: AiBomSource | null =
    (reportData?.report?.aiBom as AiBomSource | undefined) ??
    (windowAiBom as AiBomSource | null);

  // Consider data present when we have either detailed arrays OR a non-empty summary
  const hasAiBomData =
    dynamicAiBom &&
    (dynamicAiBom.totalComponents > 0 ||
      (dynamicAiBom.llmProviders?.length ?? 0) > 0 ||
      (dynamicAiBom.components?.length ?? 0) > 0 ||
      (dynamicAiBom.dependencies?.length ?? 0) > 0 ||
      Object.values(dynamicAiBom.byType ?? {}).some((v) => (v as number) > 0));

  /** Synthesize LLM component rows from detailed array → provider list → byType count */
  const resolveLLMComponents = (bom: AiBomSource): LLMComponent[] => {
    if (bom.llmComponents?.length) return bom.llmComponents;
    const fromComponents = (bom.components ?? []).filter((c: AIBOMComponent) => c.type === 'llm-api');
    if (fromComponents.length)
      return fromComponents.map((c: AIBOMComponent) => ({
        name: c.name,
        version: c.version ?? 'API',
        provider: c.provider ?? 'Unknown',
        purpose: c.description ?? 'LLM inference',
      }));
    // Fall back to summary providers
    return (bom.llmProviders ?? []).map((provider: string) => ({
      name: `${provider} API`,
      version: 'API',
      provider,
      purpose: 'LLM inference',
    }));
  };

  const API_COMPONENT_TYPES: Record<string, string> = {
    embedding: 'Embedding API',
    'vector-db': 'Vector Database',
    'rag-pipeline': 'RAG Pipeline',
    'fine-tuned-model': 'Fine-Tuned Model',
  };

  /** Synthesize API component rows from detailed array → byType summary */
  const resolveAPIComponents = (bom: AiBomSource): APIComponent[] => {
    if (bom.apiComponents?.length) return bom.apiComponents;
    const fromComponents = (bom.components ?? []).filter((c: AIBOMComponent) =>
      Object.keys(API_COMPONENT_TYPES).includes(c.type)
    );
    if (fromComponents.length)
      return fromComponents.map((c: AIBOMComponent) => ({
        name: c.name,
        version: c.version ?? 'API',
        endpoint: c.location?.file ?? 'detected',
        authentication: 'API Key',
      }));
    return Object.entries(API_COMPONENT_TYPES)
      .filter(([key]) => (bom.byType?.[key] ?? 0) > 0)
      .map(([key, label]) => ({
        name: label,
        version: 'detected',
        endpoint: `${bom.byType[key]} reference(s) found`,
        authentication: 'API Key',
      }));
  };

  const TOOL_TYPES: Record<string, string> = {
    'ai-agent': 'AI Agent',
    'mcp-server': 'MCP Server',
    'mcp-tool': 'MCP Tool',
    workflow: 'AI Workflow',
  };

  /** Synthesize system tool rows from detailed array → byType summary */
  const resolveSystemTools = (bom: AiBomSource): SystemTool[] => {
    if (bom.systemTools?.length) return bom.systemTools;
    const fromComponents = (bom.components ?? []).filter((c: AIBOMComponent) =>
      Object.keys(TOOL_TYPES).includes(c.type)
    );
    if (fromComponents.length)
      return fromComponents.map((c: AIBOMComponent) => ({
        name: c.name,
        accessLevel: 'execute' as const,
        scope: c.location?.file ?? 'detected',
        justification: c.description ?? 'AI agent/tool component',
      }));
    return Object.entries(TOOL_TYPES)
      .filter(([key]) => (bom.byType?.[key] ?? 0) > 0)
      .map(([key, label]) => ({
        name: label,
        accessLevel: 'execute' as const,
        scope: `${bom.byType[key]} instance(s) detected`,
        justification: 'Detected by static pattern analysis',
      }));
  };

  const effectiveAiBomData: AIBomData | null = hasAiBomData
    ? {
        llmComponents: resolveLLMComponents(dynamicAiBom),
        apiComponents: resolveAPIComponents(dynamicAiBom),
        libraryComponents: dynamicAiBom.libraryComponents ??
          (dynamicAiBom.dependencies ?? []).map((dep) => ({
            name: dep.name,
            version: dep.version,
            license: dep.license,
            purpose: 'Dependency',
          })),
        systemTools: resolveSystemTools(dynamicAiBom),
      }
    : null;

  // Show loading state
  if (loading) {
    return <PageSpinner label="Loading AI-BOM data…" />;
  }

  // Show message if no AI-BOM data
  if (!effectiveAiBomData) {
    return (
      <PageEmptyState
        icon={Box}
        title="No AI-BOM Data Available"
        description="AI-BOM analysis shows the AI components, APIs, and libraries used by the scanned code."
        action={
          <a
            href={'/report' + window.location.search}
            className="inline-flex items-center text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Full Report
          </a>
        }
      />
    );
  }

  const toggleVuln = (libName: string) => {
    setExpandedVulns((prev) =>
      prev.includes(libName) ? prev.filter((n) => n !== libName) : [...prev, libName]
    );
  };

  const handlePrint = () => window.print();

  const togglePrintSetting = (key: string) => {
    setPrintSettings((prev) => ({ ...prev, [key]: !prev[key as keyof PrintSettings] }));
  };

  /** Single toggle: show all when any are hidden, hide all when all are shown */
  const handleToggleAllPrint = () => {
    const allOn = settingsKeys.every((k) => printSettings[k]);
    const next = !allOn;
    setPrintSettings((prev) =>
      settingsKeys.reduce((acc, k) => ({ ...acc, [k]: next }), { ...prev })
    );
  };

  // Calculate summary stats
  const totalVulnerabilities = effectiveAiBomData.libraryComponents.reduce(
    (sum: number, lib: LibraryComponent) => sum + (lib.vulnerabilities?.length || 0),
    0
  );
  const criticalVulns = effectiveAiBomData.libraryComponents.reduce(
    (sum: number, lib: LibraryComponent) =>
      sum +
      (lib.vulnerabilities?.filter((v: VulnerabilityInfo) => v.severity === 'critical').length ||
        0),
    0
  );
  const highVulns = effectiveAiBomData.libraryComponents.reduce(
    (sum: number, lib: LibraryComponent) =>
      sum +
      (lib.vulnerabilities?.filter((v: VulnerabilityInfo) => v.severity === 'high').length || 0),
    0
  );
  const incompatibleLicenses = effectiveAiBomData.libraryComponents.filter(
    (lib: LibraryComponent) => lib.licenseDetails && !lib.licenseDetails.compatible
  ).length;

  // Origin analysis aggregation
  const originStats = effectiveAiBomData.libraryComponents.reduce(
    (
      acc: { withOrigin: number; unknown: number; byCountry: Record<string, number> },
      lib: LibraryComponent
    ) => {
      const origin = lib.origin;
      if (origin?.country) {
        acc.withOrigin++;
        acc.byCountry[origin.country] = (acc.byCountry[origin.country] || 0) + 1;
      } else {
        acc.unknown++;
      }
      return acc;
    },
    { withOrigin: 0, unknown: 0, byCountry: {} as Record<string, number> }
  );

  const uniqueCountries = Object.keys(originStats.byCountry).length;
  const topCountries = Object.entries(originStats.byCountry)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const settingsKeys = Object.keys(printSettings) as Array<keyof PrintSettings>;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .print-section {
            page-break-inside: avoid;
            margin-bottom: 16px;
          }
          .print-page-break {
            page-break-before: always;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>

      <PageBackground>
        <ReportSubHeader
          title="AI Bill of Materials (AI-BOM)"
          subtitle="Complete inventory of LLM components, APIs, libraries, and system tools"
          actions={
            <>
              <AiBomSettings
                values={printSettings as unknown as Record<string, boolean>}
                onToggle={togglePrintSetting}
                onToggleAll={handleToggleAllPrint}
              />
              <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
                <Printer className="w-4 h-4" />
                Print / Save as PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = '/report' + window.location.search)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Report
              </Button>
            </>
          }
        />
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Printable Content */}
          <div ref={printRef}>
            {/* Print Header - Only visible in print */}
            {printSettings.showHeader && (
              <div className="print-only print-section mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <img src={guardrailLogo} alt="Guardrail" className="h-8 w-auto" />
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">
                      AI Bill of Materials (AI-BOM)
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Complete inventory of LLM components, APIs, libraries, and system tools
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Generated: {new Date().toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Alert Banner if issues found */}
            {printSettings.showAlertBanner &&
              (totalVulnerabilities > 0 || incompatibleLicenses > 0) && (
                <Card className="mb-6 border-amber-500/50 bg-amber-500/5 print-section">
                  <CardContent className="p-4 flex items-center gap-4">
                    <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        Security & Compliance Issues Detected
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Found {totalVulnerabilities}{' '}
                        {pluralize(totalVulnerabilities, 'vulnerability', 'vulnerabilities')} (
                        {criticalVulns} critical, {highVulns} high) and {incompatibleLicenses}{' '}
                        potentially incompatible{' '}
                        {pluralize(incompatibleLicenses, 'license', 'licenses')}.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Summary Cards */}
            {printSettings.showSummaryCards && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8 print-section">
                <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Cpu className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {effectiveAiBomData.llmComponents.length}
                      </p>
                      <p className="text-xs text-muted-foreground">LLM Models</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Key className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {effectiveAiBomData.apiComponents.length}
                      </p>
                      <p className="text-xs text-muted-foreground">External APIs</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/20">
                      <Box className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {effectiveAiBomData.libraryComponents.length}
                      </p>
                      <p className="text-xs text-muted-foreground">Libraries</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/20">
                      <Terminal className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {effectiveAiBomData.systemTools.length}
                      </p>
                      <p className="text-xs text-muted-foreground">System Tools</p>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`bg-gradient-to-br ${getVulnerabilityCardClasses(totalVulnerabilities > 0)}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${getIconContainerClasses(totalVulnerabilities > 0, 'vulnerability')}`}
                    >
                      <ShieldAlert
                        className={`w-5 h-5 ${getIconClasses(totalVulnerabilities > 0, 'vulnerability')}`}
                      />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{totalVulnerabilities}</p>
                      <p className="text-xs text-muted-foreground">CVEs Found</p>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`bg-gradient-to-br ${getLicenseCardClasses(incompatibleLicenses > 0)}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${getIconContainerClasses(incompatibleLicenses > 0, 'license')}`}
                    >
                      <Scale
                        className={`w-5 h-5 ${getIconClasses(incompatibleLicenses > 0, 'license')}`}
                      />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{incompatibleLicenses}</p>
                      <p className="text-xs text-muted-foreground">License Issues</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Origin Analysis Section */}
            {originStats.withOrigin > 0 && (
              <Card className="mb-8 print-section">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    Dependency Origin Analysis
                    <Badge variant="outline" className="ml-2 font-normal">
                      {originStats.withOrigin} of {effectiveAiBomData.libraryComponents.length}{' '}
                      analyzed
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <p className="text-3xl font-bold text-blue-600">{uniqueCountries}</p>
                      <p className="text-sm text-muted-foreground">Countries</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                      <p className="text-3xl font-bold text-emerald-600">
                        {originStats.withOrigin}
                      </p>
                      <p className="text-sm text-muted-foreground">Origin Known</p>
                    </div>
                    <div className="text-center p-4 bg-amber-500/5 rounded-lg border border-amber-500/20">
                      <p className="text-3xl font-bold text-amber-600">{originStats.unknown}</p>
                      <p className="text-sm text-muted-foreground">Unknown Origin</p>
                    </div>
                    <div className="col-span-2 md:col-span-1 p-4 bg-zinc-500/5 rounded-lg border border-zinc-500/20">
                      <p className="text-sm font-medium mb-2">Top Countries:</p>
                      <div className="space-y-1">
                        {topCountries.map(([country, count]) => (
                          <div key={country} className="flex justify-between text-sm">
                            <span>{country}</span>
                            <Badge variant="secondary" className="ml-2">
                              {count}
                            </Badge>
                          </div>
                        ))}
                        {topCountries.length === 0 && (
                          <p className="text-xs text-muted-foreground">No origin data available</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Interactive Tabs - Hidden in Print */}
            <div className="no-print">
              <Tabs defaultValue="libraries" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="llm" className="flex items-center gap-2">
                    <Cpu className="w-4 h-4" />
                    <span className="hidden sm:inline">LLM Models</span>
                  </TabsTrigger>
                  <TabsTrigger value="api" className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    <span className="hidden sm:inline">APIs</span>
                  </TabsTrigger>
                  <TabsTrigger value="libraries" className="flex items-center gap-2 relative">
                    <Box className="w-4 h-4" />
                    <span className="hidden sm:inline">Libraries</span>
                    {(totalVulnerabilities > 0 || incompatibleLicenses > 0) && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="system" className="flex items-center gap-2">
                    <Terminal className="w-4 h-4" />
                    <span className="hidden sm:inline">System</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="llm">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-purple-600" />
                        LLM Components
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Model Name</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead>Purpose</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {effectiveAiBomData.llmComponents.map((llm, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{llm.name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono text-xs">
                                  {llm.version}
                                </Badge>
                              </TableCell>
                              <TableCell>{llm.provider}</TableCell>
                              <TableCell className="text-muted-foreground">{llm.purpose}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="api">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Key className="w-5 h-5 text-blue-600" />
                        External APIs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>API Name</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead>Endpoint</TableHead>
                            <TableHead>Authentication</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {effectiveAiBomData.apiComponents.map((api, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{api.name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono text-xs">
                                  {api.version}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs text-muted-foreground">
                                {api.endpoint}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{api.authentication}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="libraries">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Box className="w-5 h-5 text-green-600" />
                        Libraries & Dependencies
                        <span className="text-sm font-normal text-muted-foreground ml-2">
                          with vulnerability scanning & license compliance
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {effectiveAiBomData.libraryComponents.map((lib, index) => {
                        const hasVulns = lib.vulnerabilities && lib.vulnerabilities.length > 0;
                        const hasLicenseIssues =
                          lib.licenseDetails && !lib.licenseDetails.compatible;
                        const isExpanded = expandedVulns.includes(lib.name);

                        return (
                          <Collapsible
                            key={index}
                            open={isExpanded}
                            onOpenChange={() => toggleVuln(lib.name)}
                          >
                            <div
                              className={`border rounded-lg ${hasVulns || hasLicenseIssues ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'}`}
                            >
                              <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                      {hasVulns || hasLicenseIssues ? (
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                      )}
                                      <span className="font-medium">{lib.name}</span>
                                    </div>
                                    <Badge variant="outline" className="font-mono text-xs">
                                      {lib.version}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={`${
                                        lib.licenseDetails?.compatible
                                          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                          : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                      }`}
                                    >
                                      <Scale className="w-3 h-3 mr-1" />
                                      {lib.license}
                                    </Badge>
                                    {/* Origin badge - show country if available */}
                                    {lib.origin?.country && (
                                      <Badge
                                        variant="outline"
                                        className="bg-blue-500/10 text-blue-600 border-blue-500/20"
                                      >
                                        <Globe className="w-3 h-3 mr-1" />
                                        {lib.origin.country}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {hasVulns && (
                                      <Badge
                                        className={severityColors[lib.vulnerabilities![0].severity]}
                                      >
                                        {lib.vulnerabilities!.length}{' '}
                                        {pluralize(lib.vulnerabilities!.length, 'CVE', 'CVEs')}
                                      </Badge>
                                    )}
                                    {hasLicenseIssues && (
                                      <Badge
                                        variant="outline"
                                        className="border-amber-500 text-amber-600"
                                      >
                                        License Issue
                                      </Badge>
                                    )}
                                    <span className="text-xs text-muted-foreground">
                                      {isExpanded ? '▲' : '▼'}
                                    </span>
                                  </div>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="px-4 pb-4 space-y-4">
                                  <div className="text-sm text-muted-foreground">
                                    <span className="font-medium text-foreground">Purpose:</span>{' '}
                                    {lib.purpose}
                                  </div>

                                  {/* Origin Analysis Section */}
                                  {lib.origin && (
                                    <div className="space-y-2">
                                      <h4 className="font-semibold flex items-center gap-2 text-blue-600">
                                        <MapPin className="w-4 h-4" />
                                        Maintainer Origin Analysis
                                      </h4>
                                      <div className="bg-background border border-blue-500/20 rounded-lg p-3 space-y-2">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                          <div>
                                            <span className="text-muted-foreground">Country:</span>{' '}
                                            <span className="font-medium">
                                              {lib.origin.country || 'Unknown'}
                                            </span>
                                            {lib.origin.countryCode && (
                                              <span className="text-muted-foreground ml-1">
                                                ({lib.origin.countryCode})
                                              </span>
                                            )}
                                          </div>
                                          <div>
                                            <span className="text-muted-foreground">
                                              Confidence:
                                            </span>{' '}
                                            <Badge
                                              variant="outline"
                                              className={getConfidenceBadgeClasses(
                                                lib.origin.confidence
                                              )}
                                            >
                                              {lib.origin.confidence || 'unknown'}
                                            </Badge>
                                          </div>
                                          {lib.origin.maintainer && (
                                            <div>
                                              <span className="text-muted-foreground">
                                                Maintainer:
                                              </span>{' '}
                                              <span className="font-medium">
                                                {lib.origin.maintainer}
                                              </span>
                                              {lib.origin.maintainerVerified && (
                                                <CheckCircle className="w-3 h-3 inline-block ml-1 text-emerald-500" />
                                              )}
                                            </div>
                                          )}
                                          {(lib.origin.signals?.length ?? 0) > 0 && (
                                            <div>
                                              <span className="text-muted-foreground">
                                                Signals:
                                              </span>{' '}
                                              <span className="text-xs">
                                                {lib.origin.signals?.join(', ')}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Vulnerabilities Section */}
                                  {hasVulns && (
                                    <div className="space-y-2">
                                      <h4 className="font-semibold flex items-center gap-2 text-red-600">
                                        <ShieldAlert className="w-4 h-4" />
                                        Known Vulnerabilities
                                      </h4>
                                      {lib.vulnerabilities!.map((vuln, vIndex) => (
                                        <div
                                          key={vIndex}
                                          className="bg-background border border-red-500/20 rounded-lg p-3 space-y-2"
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <Badge className={severityColors[vuln.severity]}>
                                                {vuln.severity.toUpperCase()}
                                              </Badge>
                                              <a
                                                href={`https://nvd.nist.gov/vuln/detail/${vuln.cveId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-sm hover:underline flex items-center gap-1"
                                              >
                                                {vuln.cveId}
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                              Published: {vuln.publishedDate}
                                            </span>
                                          </div>
                                          <p className="text-sm text-muted-foreground">
                                            {vuln.description}
                                          </p>
                                          {vuln.fixedVersion && (
                                            <div className="flex items-center gap-2 text-sm">
                                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                                              <span>Fixed in version: </span>
                                              <Badge
                                                variant="outline"
                                                className="font-mono bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                              >
                                                {vuln.fixedVersion}
                                              </Badge>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* License Compliance Section */}
                                  {lib.licenseDetails && (
                                    <div className="space-y-2">
                                      <h4 className="font-semibold flex items-center gap-2">
                                        <Scale className="w-4 h-4" />
                                        License Compliance
                                        {lib.licenseDetails.compatible ? (
                                          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                            Compatible
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
                                            Potentially Incompatible
                                          </Badge>
                                        )}
                                      </h4>
                                      <div className="bg-background border rounded-lg p-3 space-y-3">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium">License Type:</span>
                                          <Badge variant="outline" className="capitalize">
                                            {lib.licenseDetails.type.replace('-', ' ')}
                                          </Badge>
                                        </div>

                                        {lib.licenseDetails.limitations.length > 0 && (
                                          <div>
                                            <span className="text-sm font-medium flex items-center gap-1 mb-1">
                                              <XCircle className="w-3 h-3 text-amber-500" />
                                              Limitations:
                                            </span>
                                            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                                              {lib.licenseDetails.limitations.map((lim, lIndex) => (
                                                <li key={lIndex} className="list-disc">
                                                  {lim}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {lib.licenseDetails.requirements.length > 0 && (
                                          <div>
                                            <span className="text-sm font-medium flex items-center gap-1 mb-1">
                                              <Info className="w-3 h-3 text-blue-500" />
                                              Requirements:
                                            </span>
                                            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                                              {lib.licenseDetails.requirements.map(
                                                (req, rIndex) => (
                                                  <li key={rIndex} className="list-disc">
                                                    {req}
                                                  </li>
                                                )
                                              )}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="system">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Terminal className="w-5 h-5 text-orange-600" />
                        System-Level Tools
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tool</TableHead>
                            <TableHead>Access Level</TableHead>
                            <TableHead>Scope</TableHead>
                            <TableHead>Justification</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {effectiveAiBomData.systemTools.map((tool, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium font-mono">{tool.name}</TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={accessLevelColors[tool.accessLevel]}
                                >
                                  {tool.accessLevel}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{tool.scope}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {tool.justification}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Print-Only Sections - All tabs expanded */}
            <div className="print-only space-y-6">
              {/* LLM Components */}
              {printSettings.showLLMComponents && (
                <Card className="print-section">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Cpu className="w-5 h-5 text-purple-600" />
                      LLM Components
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Model Name</TableHead>
                          <TableHead>Version</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Purpose</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {effectiveAiBomData.llmComponents.map((llm, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{llm.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">
                                {llm.version}
                              </Badge>
                            </TableCell>
                            <TableCell>{llm.provider}</TableCell>
                            <TableCell className="text-muted-foreground">{llm.purpose}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* API Components */}
              {printSettings.showAPIComponents && (
                <Card className="print-section">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Key className="w-5 h-5 text-blue-600" />
                      External APIs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>API Name</TableHead>
                          <TableHead>Version</TableHead>
                          <TableHead>Endpoint</TableHead>
                          <TableHead>Authentication</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {effectiveAiBomData.apiComponents.map((api, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{api.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">
                                {api.version}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {api.endpoint}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{api.authentication}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Libraries */}
              {printSettings.showLibraries && (
                <Card className="print-section print-page-break">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Box className="w-5 h-5 text-green-600" />
                      Libraries & Dependencies
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {effectiveAiBomData.libraryComponents.map((lib, index) => {
                      const hasVulns = lib.vulnerabilities && lib.vulnerabilities.length > 0;
                      const hasLicenseIssues = lib.licenseDetails && !lib.licenseDetails.compatible;

                      return (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 ${hasVulns || hasLicenseIssues ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'}`}
                        >
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-2">
                              {hasVulns || hasLicenseIssues ? (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              )}
                              <span className="font-medium">{lib.name}</span>
                            </div>
                            <Badge variant="outline" className="font-mono text-xs">
                              {lib.version}
                            </Badge>
                            <Badge variant="outline">{lib.license}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            <span className="font-medium text-foreground">Purpose:</span>{' '}
                            {lib.purpose}
                          </p>

                          {/* Vulnerabilities */}
                          {printSettings.showVulnerabilities && hasVulns && (
                            <div className="mt-3 space-y-2">
                              <h4 className="font-semibold text-sm text-red-600">
                                Known Vulnerabilities:
                              </h4>
                              {lib.vulnerabilities!.map((vuln, vIndex) => (
                                <div
                                  key={vIndex}
                                  className="bg-background border border-red-500/20 rounded p-2 text-sm"
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge
                                      className={severityColors[vuln.severity]}
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                    >
                                      {vuln.severity.toUpperCase()}
                                    </Badge>
                                    <span className="font-mono text-xs">{vuln.cveId}</span>
                                    {vuln.fixedVersion && (
                                      <span className="text-xs text-emerald-600">
                                        Fixed in: {vuln.fixedVersion}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {vuln.description}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* License Details */}
                          {printSettings.showLicenseDetails && lib.licenseDetails && (
                            <div className="mt-3">
                              <h4 className="font-semibold text-sm flex items-center gap-2 mb-1">
                                License: {lib.licenseDetails.type}
                                {lib.licenseDetails.compatible ? (
                                  <span className="text-emerald-600 text-xs">(Compatible)</span>
                                ) : (
                                  <span className="text-red-600 text-xs">
                                    (Potentially Incompatible)
                                  </span>
                                )}
                              </h4>
                              {lib.licenseDetails.limitations.length > 0 && (
                                <div className="text-xs">
                                  <span className="font-medium">Limitations:</span>{' '}
                                  {lib.licenseDetails.limitations.join('; ')}
                                </div>
                              )}
                              {lib.licenseDetails.requirements.length > 0 && (
                                <div className="text-xs">
                                  <span className="font-medium">Requirements:</span>{' '}
                                  {lib.licenseDetails.requirements.join('; ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* System Tools */}
              {printSettings.showSystemTools && (
                <Card className="print-section">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Terminal className="w-5 h-5 text-orange-600" />
                      System-Level Tools
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tool</TableHead>
                          <TableHead>Access Level</TableHead>
                          <TableHead>Scope</TableHead>
                          <TableHead>Justification</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {effectiveAiBomData.systemTools.map((tool, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium font-mono">{tool.name}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={accessLevelColors[tool.accessLevel]}
                              >
                                {tool.accessLevel}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{tool.scope}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {tool.justification}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </PageBackground>
    </>
  );
}

export default AIBOMPage;
